import React, {useEffect, useState} from 'react'
import {Toolbar1} from '../../../_metronic/layout/components/toolbar/Toolbar1'
import {getUsers, getUsersById} from '../../../api/AdminAPI'
import {changeTextCapital, toAbsoluteUrl} from '../../../_metronic/helpers/AssetHelpers'
import {Col, Modal, Row, Table} from 'react-bootstrap'
import {
  exportPayslip,
  getSalaryStructureValues,
  resetWorkingDays,
  sendPayslipEmail,
} from '../../../api/PayslipAPI'
import {useAuth} from '../../modules/auth'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faFileInvoice} from '@fortawesome/free-solid-svg-icons'
import {getLeavesBalance} from '../../../api/LeaveAPI'

const Payslip = () => {
  const {currentUser} = useAuth()
  const [activeUsers, setActiveUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [salaryStructureValues, setSalaryStructureValues] = useState<SalaryStructureValue | null>(
    null
  )
  const [lossOfPay, setLossOfPay] = useState<number>(0)
  const [unpaidLeave, setUnpaidLeave] = useState<number>(0)
  const [workingDays, setWorkingDays] = useState<number | null>(null)

  useEffect(() => {
    const fetchActiveUsers = async () => {
      try {
        const response = await getUsers()
        if (response && Array.isArray(response.data)) {
          setActiveUsers(response.data)
        } else {
          setError('Invalid response structure')
        }
      } catch (error) {
        console.error('Error fetching active users:', error)
        setError('Error fetching active users')
      } finally {
        setLoading(false)
      }
    }

    fetchActiveUsers()
  }, [])

  useEffect(() => {
    const fetchLeaveBalance = async (userId) => {
      try {
        const userData = await getLeavesBalance(userId)
        const leaveData = userData.data.users
        setUnpaidLeave(leaveData.unpaidLeave || 0)
        setLossOfPay(leaveData.lossOfPay || 0)
      } catch (error) {
        console.error('Error fetching leave balance:', error)
      }
    }

    if (selectedUser?.id) {
      fetchLeaveBalance(selectedUser?.id)
    }
  }, [selectedUser?.id])

  useEffect(() => {
    const fetchWorkingDays = async () => {
      try {
        const response = await resetWorkingDays()
        const workingDaysCount = response.data.workingDays
        setWorkingDays(workingDaysCount)
      } catch (error) {
        console.error('Error fetching working days:', error)
      }
    }

    fetchWorkingDays()
  }, [])

  const handleOpenModal = async (user: User) => {
    setSelectedUser(user)
    setShowModal(true)
    try {
      const userData = await getUsersById(user.id)
      const salaryValues = await getSalaryStructureValues(user.id, currentUser)
      setSalaryStructureValues(salaryValues)
    } catch (error) {
      console.error('Error fetching user data:', error)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedUser(null)
    setSalaryStructureValues(null)
  }

  const handleGeneratePayslip = async () => {
    if (selectedUser) {
      try {
        await sendPayslipEmail({
          sender: selectedUser,
          subject: 'Your Payslip',
          body: `Hello ${selectedUser.firstName}, please find attached your payslip.`,
        })
        alert('Payslip email sent successfully!')
        handleCloseModal()
      } catch (error) {
        console.error('Error sending payslip email:', error)
        alert('Sending payslip to mail.')
      }
    }
  }

  const handleExportPayslip = async () => {
    if (selectedUser) {
      try {
        await exportPayslip(selectedUser.id)
        alert('Payslip downloaded successfully!')
        handleCloseModal()
      } catch (error) {
        console.error('Error exporting payslip:', error)
        alert('Failed to download payslip.')
      }
    }
  }

  const calculateTotalEarnings = (values: SalaryStructureValue) => {
    return (
      (Number(values.basicValue) || 0) +
      (Number(values.HRAValue) || 0) +
      (Number(values.educationValue) || 0) +
      (Number(values.DAValue) || 0) +
      (Number(values.LTAValue) || 0) +
      (Number(values.specialAllowanceValue) || 0) +
      (Number(values.variableValue) || 0) +
      (Number(values.telephoneAndInternetReimbursementValue) || 0) +
      (Number(values.carAndDriverReimbursementValue) || 0) +
      (Number(values.mealCouponValue) || 0) +
      (Number(values.booksAndPeriodicalsValue) || 0)
    )
  }

  const calculateTotalDeductions = (values: SalaryStructureValue) => {
    return (
      (Number(values.PFValue) || 0) +
      (Number(values.PTValue) || 0) +
      (Number(values.ESICValue) || 0) +
      (Number(values.LWFValue) || 0) +
      (lossOfPay || 0)
    )
  }

  const calculateNetSalary = (values: SalaryStructureValue | null) => {
    if (!values) {
      return 0
    }
    const totalEarnings = calculateGrossSalary(values)
    const totalDeductions = calculateTotalDeductions(values)
    return Number((totalEarnings - totalDeductions).toFixed(2))
  }

  const calculateMonthlyValue = (annualValue: number | undefined): string => {
    if (annualValue === undefined) {
      return '0.00'
    }
    const annual = Number(annualValue) || 0
    return (annual / 12).toFixed(2)
  }

  const calculateGrossSalary = (values: SalaryStructureValue) => {
    const totalEarnings = calculateTotalEarnings(values)
    const totalDeductions = calculateTotalDeductions(values)
    return Number((totalEarnings + totalDeductions).toFixed(2)) 
  }

  if (loading) {
    return <div>Loading...</div>
  }

  if (error) {
    return <div>{error}</div>
  }

  if (!currentUser) {
    return null
  }

  return (
    <>
      <Toolbar1 />
      <div className='container mt-5'>
        <h2>Current User</h2>
        <div className='table-responsive'>
          <table className='table table-striped table-bordered'>
            <thead>
              <tr className='fw-bold text-muted'>
                <th className='text-center' style={{minWidth: '50px'}}>
                  {changeTextCapital('Sr.No')}
                </th>
                <th className='text-center' style={{minWidth: '150px'}}>
                  {changeTextCapital('Name')}
                </th>
                <th className='text-center' style={{minWidth: '200px'}}>
                  {changeTextCapital('Email')}
                </th>
                <th className='pl-1 text-center text-muted' style={{minWidth: '100px'}}>
                  {changeTextCapital('Actions')}
                </th>
              </tr>
            </thead>
            <tbody className='text-gray-800 fw-semibold'>
              {currentUser ? (
                <tr key={currentUser.id}>
                  <td className='text-center'>1</td>
                  <td className='text-center'>
                    {currentUser.firstName} {currentUser.lastName}
                  </td>
                  <td className='text-center'>{currentUser.email}</td>
                  <td>
                    <div className='d-flex justify-content-center flex-shrink-0'>
                      <button
                        className='btn btn-icon btn-bg-light btn-active-color-info btn-sm me-1'
                        onClick={() => {
                          handleOpenModal(currentUser)
                        }}
                      >
                        <FontAwesomeIcon icon={faFileInvoice} />
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={4} className='text-center'>
                    No Active Users Available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} size='lg'>
        <Modal.Body>
          <div className='container'>
            <div className='row py-0'>
              <div className='col-md-3 py-0'>
                <img
                  src={toAbsoluteUrl('/media/logos/skeletosLogo.png')}
                  alt='Skeletos'
                  className='img-fluid'
                  style={{height: '120px', width: '120px', marginTop: '-20px'}}
                />
              </div>
              <div className='col-md-1 d-flex align-items-center'>
                <div style={{borderLeft: '1px solid #000', height: '100%'}}></div>
              </div>
              <div className='col-md-8'>
                <h2>Skeletos IT Services LLP</h2>
                <h6>Software Company in Pune, Maharashtra</h6>
                <p>
                  Skeletos IT Services LLP Level 02, Shakuntal Appt, Law College Road,Near Pastry
                  Corner, Nal Stop, Erandwane, India, MH, Pune: 411004
                </p>
              </div>
            </div>
          </div>
          <hr />
          {selectedUser && (
            <div>
              <div>
                <Row>
                  <Col md={6}>
                    <Table striped bordered hover size='sm'>
                      <tbody>
                        <tr>
                          <td>
                            <strong>Employee ID:</strong>
                          </td>
                          <td>{selectedUser.id || '-'}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Employee Name:</strong>
                          </td>
                          <td>{`${selectedUser.firstName} ${selectedUser.lastName}` || '-'}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Designation:</strong>
                          </td>
                          <td>{selectedUser.designation?.name || '-'}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Department:</strong>
                          </td>
                          <td>{selectedUser.designation?.name || '-'}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Joining Date:</strong>
                          </td>
                          <td>{selectedUser.startDate || '-'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={6}>
                    <Table striped bordered hover size='sm'>
                      <tbody>
                        <tr>
                          <td>
                            <strong>UNA:</strong>
                          </td>
                          <td>-</td>
                          {/* <td>{selectedUser.una || '-'}</td> */}
                        </tr>
                        <tr>
                          <td>
                            <strong>PF No.:</strong>
                          </td>
                          <td>-</td>
                          {/* <td>{selectedUser.pfNo || '-'}</td> */}
                        </tr>
                        <tr>
                          <td>
                            <strong>ESI No.:</strong>
                          </td>
                          <td>-</td>
                          {/* <td>{selectedUser.esiNo || '-'}</td> */}
                        </tr>
                        <tr>
                          <td>
                            <strong>Bank Name:</strong>
                          </td>
                          <td>{selectedUser.bankName || '-'}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Bank A/c No:</strong>
                          </td>
                          <td>{selectedUser.accountNumber || '-'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </div>
              <hr />
              <div>
                <Row>
                  <Col md={6}>
                    <Table striped bordered hover size='sm'>
                      <tbody>
                        <tr>
                          <td>
                            <strong>Gross Salary:</strong>
                          </td>
                          <td>
                            {salaryStructureValues
                              ? calculateGrossSalary(salaryStructureValues)
                              : '-'}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Total Working Days:</strong>
                          </td>
                          <td>{workingDays !== null ? workingDays : '-'}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>LOP(Days):</strong>
                          </td>
                          <td>{unpaidLeave}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={6}>
                    <Table striped bordered hover size='sm'>
                      <tbody>
                        <tr>
                          <td>
                            <strong>Leaves:</strong>
                          </td>
                          <td>-</td>
                          {/* <td>{selectedUser.pfNo || '-'}</td> */}
                        </tr>
                        <tr>
                          <td>
                            <strong>Paid Days:</strong>
                          </td>
                          <td>30</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </div>
              <h4>Salary Structure</h4>
              <hr />
              {salaryStructureValues ? (
                <>
                  <Row>
                    <Col md={6}>
                      <Table striped bordered hover size='sm'>
                        <thead className='table-info'>
                          <tr>
                            <th>Earning Component</th>
                            <th>Monthly Amount</th>
                            <th>Annual Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Basic</td>
                            <td>{calculateMonthlyValue(salaryStructureValues.basicValue)}</td>
                            <td>{(Number(salaryStructureValues.basicValue) || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>HRA</td>
                            <td>{calculateMonthlyValue(salaryStructureValues.HRAValue)}</td>
                            <td>{(Number(salaryStructureValues.HRAValue) || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>Education</td>
                            <td>{calculateMonthlyValue(salaryStructureValues.educationValue)}</td>
                            <td>
                              {(Number(salaryStructureValues.educationValue) || 0).toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>DA</td>
                            <td>{calculateMonthlyValue(salaryStructureValues.DAValue)}</td>
                            <td>{(Number(salaryStructureValues.DAValue) || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>LTA</td>
                            <td>{calculateMonthlyValue(salaryStructureValues.LTAValue)}</td>
                            <td>{(Number(salaryStructureValues.LTAValue) || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>Special Allowance</td>
                            <td>
                              {calculateMonthlyValue(salaryStructureValues.specialAllowanceValue)}
                            </td>
                            <td>
                              {(Number(salaryStructureValues.specialAllowanceValue) || 0).toFixed(
                                2
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td>Variable</td>
                            <td>{calculateMonthlyValue(salaryStructureValues.variableValue)}</td>
                            <td>{(Number(salaryStructureValues.variableValue) || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>Telephone and Internet Reimbursement</td>
                            <td>
                              {calculateMonthlyValue(
                                salaryStructureValues.telephoneAndInternetReimbursementValue
                              )}
                            </td>
                            <td>
                              {(
                                Number(
                                  salaryStructureValues.telephoneAndInternetReimbursementValue
                                ) || 0
                              ).toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>Car and Driver Reimbursement</td>
                            <td>
                              {calculateMonthlyValue(
                                salaryStructureValues.carAndDriverReimbursementValue
                              )}
                            </td>
                            <td>
                              {(
                                Number(salaryStructureValues.carAndDriverReimbursementValue) || 0
                              ).toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>Meal Coupon</td>
                            <td>{calculateMonthlyValue(salaryStructureValues.mealCouponValue)}</td>
                            <td>
                              {(Number(salaryStructureValues.mealCouponValue) || 0).toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>Books and Periodicals</td>
                            <td>
                              {calculateMonthlyValue(
                                salaryStructureValues.booksAndPeriodicalsValue
                              )}
                            </td>
                            <td>
                              {(
                                Number(salaryStructureValues.booksAndPeriodicalsValue) || 0
                              ).toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Total Earnings</strong>
                            </td>
                            <td>
                              <strong>
                                {calculateMonthlyValue(
                                  calculateTotalEarnings(salaryStructureValues)
                                )}
                              </strong>
                            </td>
                            <td>
                              <strong>
                                {calculateTotalEarnings(salaryStructureValues).toFixed(2)}
                              </strong>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6}>
                      <Table striped bordered hover size='sm'>
                        <thead className='table-info'>
                          <tr>
                            <th>Deduction Component</th>
                            <th>Monthly Amount</th>
                            <th>Annual Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>PF</td>
                            <td>{calculateMonthlyValue(salaryStructureValues.PFValue)}</td>
                            <td>{(Number(salaryStructureValues.PFValue) || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>PT</td>
                            <td>{calculateMonthlyValue(salaryStructureValues.PTValue)}</td>
                            <td>{(Number(salaryStructureValues.PTValue) || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>ESIC</td>
                            <td>{calculateMonthlyValue(salaryStructureValues.ESICValue)}</td>
                            <td>{(Number(salaryStructureValues.ESICValue) || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>LWF</td>
                            <td>{calculateMonthlyValue(salaryStructureValues.LWFValue)}</td>
                            <td>{(Number(salaryStructureValues.LWFValue) || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Total Deductions</strong>
                            </td>
                            <td>
                              <strong>
                                {calculateMonthlyValue(
                                  calculateTotalDeductions(salaryStructureValues)
                                )}
                              </strong>
                            </td>
                            <td>
                              <strong>
                                {calculateTotalDeductions(salaryStructureValues).toFixed(2)}
                              </strong>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                      <Table striped bordered hover size='sm'>
                        <thead>
                          <tr>
                            <th>Loss of Pay (quarterly)</th>
                            <th>{lossOfPay.toFixed(2)}</th>
                          </tr>
                        </thead>
                      </Table>
                    </Col>
                  </Row>
                  <Row className='mt-4'>
                    <Col md={6}>
                      <h5>Net Salary (Gross Salary - Deductions)</h5>
                      <Table striped bordered hover>
                        <tbody>
                          <tr>
                            <td>
                              <strong>Net Salary</strong>
                            </td>
                            <td>
                              <strong>{calculateNetSalary(salaryStructureValues)}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </>
              ) : (
                <div>No Salary Structure available for user.</div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{padding: '0.5rem 1rem'}}>
          <button
            type='button'
            className='btn btn-secondary btn-sm'
            onClick={handleCloseModal}
            style={{margin: '0'}}
          >
            Close
          </button>
          <button
            type='button'
            className='btn btn-primary btn-sm ms-2'
            onClick={handleGeneratePayslip}
            disabled={!salaryStructureValues}
            style={{margin: '0'}}
          >
            Generate Payslip
          </button>
          <button
            type='button'
            className='btn btn-success btn-sm ms-2'
            onClick={handleExportPayslip}
            disabled={!salaryStructureValues}
            style={{margin: '0'}}
          >
            Export as PDF
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default Payslip
